import { Box, Stack } from "@mui/material";
import { Button, Card, Typography } from "@platform/design-system";
import { useRef } from "react";

import { useCensusUpload } from "../hooks/useCensusUpload";

interface CensusUploadButtonProps {
  readonly rfpId: string;
  /** Wording changes with whether this replaces a census or supplies the first. */
  readonly hasCensus: boolean;
  /**
   * Called with the stored document once a read has started, so the caller
   * can wait for that document rather than for any newer read.
   */
  readonly onUploaded?: (documentId: number) => void;
}

const ACCEPTED = ".xlsx,.xls,.csv";

/**
 * Uploads a census for this RFP.
 *
 * Reading happens in a workflow, so a successful upload means the file was
 * stored and reading has started — the page will not show the new census until
 * that finishes and the query is refetched.
 */
export const CensusUploadButton = ({
  rfpId,
  hasCensus,
  onUploaded,
}: CensusUploadButtonProps): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading } = useCensusUpload(rfpId, onUploaded);

  const onFileChosen = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      upload(file);
    }

    // Clear the input so choosing the same file again still fires a change.
    event.target.value = "";
  };

  return (
    <Card sx={{ p: 2 }} data-testid="census-upload">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        hidden
        onChange={onFileChosen}
        data-testid="census-upload-input"
      />

      <Stack gap={1.5}>
        <Stack gap={0.5}>
          <Typography variant="subtitle1">
            {hasCensus ? "Replace census" : "Upload census"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {hasCensus
              ? "A new file replaces the census this RFP is quoted on and re-runs every check. The previous read is kept."
              : "Upload the broker's census to have it read and checked. It is normally read automatically when an RFP is submitted."}
          </Typography>
        </Stack>

        <Box>
          <Button
            variant="secondary"
            disabled={isUploading}
            onClick={() => inputRef.current?.click()}
            data-testid="census-upload-button"
          >
            {isUploading ? "Uploading…" : "Choose a file"}
          </Button>
        </Box>
      </Stack>
    </Card>
  );
};
